import {
  configureStore, createListenerMiddleware
} from '@reduxjs/toolkit';
import { rememberReducer, rememberEnhancer } from 'redux-remember';

import { sentryEnhancer } from '@/services/logger/sentry';
import { directPaymentSlice } from '@/features/directPayment/directPaymentSlice';
import { gocardlessSlice } from '@/providers/gocardless/gocardlessSlice';
import { lookupsApi } from '@/api/lookups';
import { payinSlice } from '@/features/payin/payinSlice';
import { paymentsApi } from '@/api/payments';
import { cartsApi } from '@/api/carts';
import { paymentSelectSlice } from '@/features/paymentSelect/paymentSelectSlice';
import { seoSlice } from '@/services/seo/seoSlice';
import { transactionSlice } from '@/features/paymentProcess/transactionSlice';
import { urlOverridesSlice } from '@/services/urlOverrides/urlOverridesSlice';
import { userDetailsSlice } from '@/features/userDetails/userDetailsSlice';
import { shippingDetailsSlice } from '@/features/shippingDetails/shippingDetailsSlice';
import { gtmSlice, GtmEventChangeInterface } from '@/services/gtm/gtmSlice';
import { journeysSlice } from '@/services/journeys/journeysSlice';
import { HandleGtmEvents } from '@/services/gtm/handleGTMEvents';
import { HandleGtmPayinEvents } from '@/services/gtm/handleGTMPayinEvents';

import { JOURNEY_TYPES } from '@/constants/constants';

export interface CustomAction {
  type: string,
  payload: GtmEventChangeInterface
}

const gtmMiddleware = createListenerMiddleware();

// Attach a listener to our generic GTM event:
gtmMiddleware.startListening({
  type: 'gtm/setGtmEventChange',
  effect: async (action, listenerApi) => {
    const currentState = listenerApi.getState() as RootState;
    const thisCustomAction = action as CustomAction;

    // Ensure GTM is ready first:
    if (currentState.gtm.gtmInstantiated) {
      // Use the appropriate event handler:
      if (currentState.journeys.journey === JOURNEY_TYPES.PAYIN) {
        HandleGtmPayinEvents(currentState, thisCustomAction);
      } else {
        HandleGtmEvents(currentState, thisCustomAction);
      }
    }
  },
});

const persistedSlices = [
  directPaymentSlice.reducerPath,
  gocardlessSlice.reducerPath,
  journeysSlice.reducerPath,
  lookupsApi.reducerPath,
  payinSlice.reducerPath,
  paymentsApi.reducerPath,
  paymentSelectSlice.reducerPath,
  seoSlice.reducerPath,
  transactionSlice.reducerPath,
  urlOverridesSlice.reducerPath,
  userDetailsSlice.reducerPath,
  shippingDetailsSlice.reducerPath,
];

const reducers = rememberReducer({
  [gtmSlice.reducerPath]: gtmSlice.reducer,
  [directPaymentSlice.reducerPath]: directPaymentSlice.reducer,
  [gocardlessSlice.reducerPath]: gocardlessSlice.reducer,
  [journeysSlice.reducerPath]: journeysSlice.reducer,
  [lookupsApi.reducerPath]: lookupsApi.reducer,
  [payinSlice.reducerPath]: payinSlice.reducer,
  [paymentsApi.reducerPath]: paymentsApi.reducer,
  [cartsApi.reducerPath]: cartsApi.reducer,
  [paymentSelectSlice.reducerPath]: paymentSelectSlice.reducer,
  [seoSlice.reducerPath]: seoSlice.reducer,
  [transactionSlice.reducerPath]: transactionSlice.reducer,
  [urlOverridesSlice.reducerPath]: urlOverridesSlice.reducer,
  [userDetailsSlice.reducerPath]: userDetailsSlice.reducer,
  [shippingDetailsSlice.reducerPath]: shippingDetailsSlice.reducer,
});

export const store = configureStore({
  reducer: reducers,
  middleware: getDefaultMiddleware => (
    getDefaultMiddleware()
      .concat([
        gtmMiddleware.middleware,
        lookupsApi.middleware,
        paymentsApi.middleware,
        cartsApi.middleware,
      ])
  ),
  enhancers: getDefaultEnhancers => getDefaultEnhancers().concat(
    rememberEnhancer(
      window.sessionStorage,
      persistedSlices,
    ),
    sentryEnhancer(),
  ),
});

// Get the type of our store variable
export type AppStore = typeof store;

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<AppStore['getState']>;

export type AppDispatch = AppStore['dispatch'];
